/**
 * Life4Earth V2 — Verification window API adapter
 *
 * Frontend client for the skill-matched Verification window — the PULL surface
 * (DEV-1379, Content Verification Stage 3). See SPEC_CONTENT_VERIFICATION.md
 * §4 Stage 3, §8, §6.
 *
 * The window is HIDDEN for minors: fetchVerificationPool() throws an ApiError
 * with status 403 for under-18 / null-DOB callers; the page renders a
 * "not available" state in that case.
 *
 * Talks to:
 *   GET  /api/verification/pool    → items needing review + claim state + matchText
 *   POST /api/verification/claim   → claim an item (≤10 active claims cap; 409 over cap)
 *   POST /api/verification/return  → return a claimed item to the pool
 *   POST /api/chronicles/{guid}/submit → author submits content into the pool
 */

import { apiFetch } from './client';

// ── Types ──────────────────────────────────────────────────────────

/** An item in the review pool, with claim state and match text. */
export interface VerificationPoolItem {
  chronicleGuid: string;
  title: string;
  categoryKey: string;
  reviewStatus: string;
  reviewModel: string;
  requiredApprovals: number;
  currentApprovedCount: number;
  /**
   * The text the frontend matcher tokenizes. When the author declared SUBJECT
   * tags (DEV-1396) this is their platform-skill labels (the required-skill set,
   * direct-matched to reviewer endorsements); otherwise title + category + tags.
   */
  matchText: string;
  tags: string[];
  /** Author-declared subject SLUGS (DEV-1396) — platform_skills.slug; [] when none. */
  subjects: string[];
  /** Resolved platform-skill labels for `subjects` (display + match). */
  subjectLabels: string[];
  claimedByMe: boolean;
  activeClaimCount: number;
}

export interface ClaimResult {
  claimGuid?: string;
  contentGuid: string;
  reviewStatus?: string;
  activeClaimCount?: number;
  maxActiveClaims?: number;
  alreadyClaimed?: boolean;
}

// ── API Functions ──────────────────────────────────────────────────

/**
 * Fetch the review pool for the caller. Excludes content the caller authored
 * or has already reviewed. Throws ApiError(403) for minors (window hidden).
 */
export function fetchVerificationPool(): Promise<VerificationPoolItem[]> {
  return apiFetch<VerificationPoolItem[]>('/api/verification/pool');
}

/** Claim an item from the pool. 409 when the caller is at the active-claim cap. */
export function claimReviewItem(contentGuid: string): Promise<ClaimResult> {
  return apiFetch<ClaimResult>('/api/verification/claim', {
    method: 'POST',
    body: JSON.stringify({ contentGuid }),
  });
}

/** Return a claimed item to the pool. */
export function returnReviewItem(contentGuid: string): Promise<{ contentGuid: string; returned: boolean }> {
  return apiFetch<{ contentGuid: string; returned: boolean }>('/api/verification/return', {
    method: 'POST',
    body: JSON.stringify({ contentGuid }),
  });
}

/** Author action: submit content into the review pool (draft|changes_requested → submitted). */
export function submitChronicleForReview(
  chronicleGuid: string,
): Promise<{ chronicleGuid: string; reviewStatus: string }> {
  return apiFetch<{ chronicleGuid: string; reviewStatus: string }>(
    `/api/chronicles/${chronicleGuid}/submit`,
    { method: 'POST' },
  );
}

// ── Verdict (Stage 1 endpoint, consumed from the Verification window Tray) ──

export interface ReviewVerdictRequest {
  verdict: 'approved' | 'changes_requested' | 'rejected';
  notes?: string;
  // DEV-1392: reviewModel removed — the server derives the bar from the
  // chronicle's stored template_key; clients no longer supply it.
}

export interface ReviewVerdictResult {
  chronicleGuid: string;
  reviewStatus: string;
  approvedCount: number;
  requiredApprovals: number;
}

/**
 * Record a verdict on a piece of content (POST /api/chronicles/{guid}/review).
 * The server enforces 18+, review authority, and the conflict-of-interest guard.
 */
export function reviewChronicle(
  chronicleGuid: string,
  req: ReviewVerdictRequest,
): Promise<ReviewVerdictResult> {
  return apiFetch<ReviewVerdictResult>(`/api/chronicles/${chronicleGuid}/review`, {
    method: 'POST',
    body: JSON.stringify(req),
  });
}

// ── Gate 1 — community-standards review (DEV-1401 / -1402 / -1403) ──
//
// Distinct from the expert pool/verdict above (Gate 2, gates SEARCH). Gate 1 is
// per-community and gates a single COMMUNITY's FEED. Eligibility = Contributor+
// active member of that community (18+ interim). The verification-window UI and
// reach-ladder UI are a SEPARATE later increment — these client functions exist
// so that increment can consume the endpoints. No UI here.

/** An item in the Gate-1 community-standards pool. */
export interface CommunityPoolItem {
  chronicleGuid: string;
  title: string;
  categoryKey: string;
  reviewStatus: string;
  /** The community whose feed a Gate-1 approval here would unlock. */
  communityGuid: string;
  communityName?: string | null;
}

export interface CommunityReviewRequest {
  contentGuid: string;
  communityGuid: string;
  verdict: 'approved' | 'changes_requested' | 'rejected';
  notes?: string;
}

export interface CommunityReviewResult {
  reviewGuid: string;
  contentGuid: string;
  communityGuid: string;
  verdict: string;
  /** True when this approval pinned the chronicle's validated core (§12.11). */
  corePinned: boolean;
}

/**
 * Fetch the Gate-1 community-standards pool for the caller — chronicles awaiting
 * community-standards review for communities the caller is Gate-1-eligible for.
 * Throws ApiError(403) for under-18 callers (window hidden until DEV-926).
 */
export function fetchCommunityReviewPool(): Promise<CommunityPoolItem[]> {
  return apiFetch<CommunityPoolItem[]>('/api/verification/community-pool');
}

/**
 * Record a Gate-1 community-standards verdict for (content, community). The
 * server enforces Gate-1 eligibility, the conflict-of-interest guard, and the
 * home-community match; on approval it pins the validated core (§12.11) if absent.
 */
export function reviewCommunityStandards(
  req: CommunityReviewRequest,
): Promise<CommunityReviewResult> {
  return apiFetch<CommunityReviewResult>('/api/verification/community-review', {
    method: 'POST',
    body: JSON.stringify(req),
  });
}
